import { BOT_USER_IDS } from '@joyful/utils'
import type { ComponentProps, ReactElement } from 'react'
import type { components } from '@joyful/openapi/types.gen'
import { BotIcon, CircleUserRoundIcon, SquareUserRoundIcon } from 'lucide-react'
import type { AvatarProps } from './Avatar'
import { Avatar, AvatarFallback, AvatarImage } from './Avatar'
import type { IconBubbleProps } from './IconBubble'
import { IconBubble } from './IconBubble'
import { Slot } from './Slot'

export interface UserAvatarProps extends Omit<AvatarProps, 'children'> {
  imageUrl: string | null
  isCurrentUser?: boolean
  name: string | null
  userId: string
}

export function UserAvatar({
  imageUrl,
  isCurrentUser = false,
  name,
  ring,
  userId,
  ...restProps
}: UserAvatarProps) {
  if (BOT_USER_IDS.has(userId)) {
    return <JoyfulBotAvatar size="md" {...restProps} />
  }

  return (
    <Avatar variant="round" ring={ring} {...restProps}>
      <AvatarImage src={imageUrl ?? undefined} alt={name ?? ''} />
      <AvatarFallback tone={isCurrentUser ? undefined : 'muted'}>
        {name?.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  )
}

export type AgentAvatarProps = Omit<IconBubbleProps, 'children' | 'variant'>

export type JoyfulHealthAvatarProps = Omit<AvatarProps, 'children'>

export type JoyfulBotAvatarProps = Omit<
  AvatarProps,
  'children' | 'ref' | 'ring' | 'variant'
>

const joyfulBotAvatarSizeByAvatarSize: Record<
  NonNullable<AvatarProps['size']>,
  NonNullable<AgentAvatarProps['size']>
> = {
  lg: 'md',
  md: 'sm',
  sm: 'xs',
}

export function JoyfulBotAvatar({
  size = 'md',
  ...props
}: JoyfulBotAvatarProps) {
  return <AgentAvatar size={joyfulBotAvatarSizeByAvatarSize[size]} {...props} />
}

export function JoyfulHealthAvatar(props: JoyfulHealthAvatarProps) {
  return (
    <Avatar {...props}>
      <AvatarFallback>{`J`}</AvatarFallback>
    </Avatar>
  )
}

// Bubble used by the encounter timeline (and anywhere else we attribute
// activity to the AI agent rather than a human or a bot user).
export function AgentAvatar({ size = 'sm', ...restProps }: AgentAvatarProps) {
  return (
    <IconBubble {...restProps} variant="agent" size={size}>
      <BotIcon />
    </IconBubble>
  )
}

export const BillerUserIcon = SquareUserRoundIcon

export interface OptionalUserAvatarProps extends Omit<
  UserAvatarProps,
  'imageUrl' | 'name' | 'userId' | 'orgRole'
> {
  fallback?: ReactElement
  user: Pick<
    components['schemas']['EventUser'],
    'id' | 'image_url' | 'name' | 'organization_role'
  > | null
}

export function OptionalUserAvatar({
  fallback,
  user,
  ...props
}: OptionalUserAvatarProps) {
  if (user === null) {
    if (fallback) {
      return <Slot {...props}>{fallback}</Slot>
    }

    return (
      <span className="text-tremor-content">
        <CircleUserRoundIcon className="size-full" />
      </span>
    )
  }

  return (
    <UserAvatar
      imageUrl={user.image_url ?? null}
      name={user.name ?? null}
      userId={user.id}
      {...props}
    />
  )
}

export function UserName({
  user,
}: {
  user: Pick<components['schemas']['UserRead'], 'name'>
}) {
  return user.name
}

export interface OptionalUserNameProps extends Omit<
  ComponentProps<'span'>,
  'children'
> {
  user: Pick<components['schemas']['UserRead'], 'name'> | null
}

export function OptionalUserName({
  user,
  ...restProps
}: OptionalUserNameProps) {
  if (user === null) {
    return <span {...restProps}>{`Joyful Health`}</span>
  }

  return (
    <strong {...restProps}>
      <UserName user={user} />
    </strong>
  )
}
